import { createContext, useContext } from 'react';

import type { AiChat, AiChatDetail } from 'src/types';

// ----------------------------------------------------------------------

export type ChatItem =
  | { kind: 'user'; id: string; text: string }
  | { kind: 'assistant'; id: string; text: string }
  | { kind: 'tools-loaded'; id: string; names: string[] }
  | {
      kind: 'tool';
      id: string;
      toolName: string;
      status: 'running' | 'done' | 'error' | 'denied' | 'rejected';
    }
  | {
      kind: 'confirmation';
      id: string;
      toolName: string;
      args: unknown;
      state: 'pending' | 'approved' | 'rejected';
    }
  | { kind: 'image'; id: string; path: string }
  | { kind: 'error'; id: string; messageKey: 'generic' | 'aborted' | 'context-full' };

export type OrchestratorPhase = 'idle' | 'planning' | 'thinking' | 'awaiting-confirmation';

export type ContextUsage = {
  used: number;
  max: number;
};

export type AiOrchestratorContextValue = {
  isOpen: boolean;
  open: VoidFunction;
  close: VoidFunction;
  items: ChatItem[];
  phase: OrchestratorPhase;
  contextUsage: ContextUsage;
  isContextFull: boolean;
  activeChatId: AiChat['id'] | null;
  send: (text: string) => void;
  stop: VoidFunction;
  startNewChat: VoidFunction;
  restoreChat: (chat: AiChatDetail) => boolean;
  resolveConfirmation: (itemId: string, approved: boolean) => void;
};

export const AiOrchestratorContext = createContext<AiOrchestratorContextValue | null>(null);

export const useAiOrchestrator = (): AiOrchestratorContextValue => {
  const context = useContext(AiOrchestratorContext);
  if (!context) {
    throw new Error('useAiOrchestrator must be used within AiOrchestratorProvider');
  }
  return context;
};
